import { computed } from 'vue';

import { useStorefrontCollectionProducts } from '@/shared/composables/useStorefrontCollectionProducts';
import { GIFT_COLLECTION_FETCH_WINDOW } from '@/shared/constants/constants';

/**
 * Shared, market-aware "are there any gifts the buyer can actually receive?" signal.
 *
 * Wraps {@link useStorefrontCollectionProducts}, which fetches the gift collection
 * `@inContext(country)` and filters out anything that is not `availableForSale`.
 * `hasAvailableGifts` is therefore true only once the fetch has resolved AND at
 * least one purchasable product survived the market-aware transform.
 *
 * Both cart surfaces (the progress bar via ThresholdVisualizer and the gift picker
 * via GiftCollectionDisplay) consume this with the same `collectionHandle` + country,
 * so Apollo's normalized cache serves the second consumer without a second request.
 *
 * Fail-safe: while loading, or on query error, `hasAvailableGifts` is false so callers
 * never advertise a gift that cannot be fulfilled. Callers upgrade only on confirmed
 * availability.
 *
 * The fetch window matches the picker's ({@link GIFT_COLLECTION_FETCH_WINDOW}) so the two
 * surfaces agree on the exact set of products and share Apollo's cache (one request).
 *
 * @param collectionHandle - Gift collection handle (already market-resolved in Liquid)
 * @param options.first - Page size for the availability check (default {@link GIFT_COLLECTION_FETCH_WINDOW})
 */
export const useGiftAvailability = (
  collectionHandle: string,
  options?: { first?: number }
) => {
  const { products, loading, error } = useStorefrontCollectionProducts(
    collectionHandle,
    { first: options?.first ?? GIFT_COLLECTION_FETCH_WINDOW }
  );

  const hasAvailableGifts = computed(() => {
    // Fail safe: not resolved, errored, or no handle => treat as no gift.
    if (loading.value || error.value) return false;
    if (!collectionHandle) return false;
    return products.value.length > 0;
  });

  return {
    hasAvailableGifts,
    loading,
    error
  };
};
